import { createDeepEqualSelector, RootState } from '@/store/store';
import {
    ClientStorageState,
    WALLET_DISPLAY_MODE,
    WIDGETS,
} from '@/state/clientStorage/clientStorageTypes';
import {
    initialAdvancedFiltersEmpty,
    initialDate,
    initialSymbolsEmpty,
} from '@/state/clientStorage/clientStorageSlice';
import { selectMemberProfile } from '@/state/member/memberSelectors';

const clientStorageSelector = (state: RootState): ClientStorageState => state.clientStorage;

export const selectIsLoading = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => clientStorage.isLoading,
);

export const selectClientStorage = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => {
        return clientStorage.clientStorage;
    },
);

export const selectCurrentAdvancedSelection = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => {
        return clientStorage.currentAdvancedSelection;
    },
);

export const selectAdvancedFilter = createDeepEqualSelector(
    [
        selectClientStorage,
        (state, { accountId, isMainPage = false }: { accountId: number; isMainPage?: boolean }) => {
            return { accountId, isMainPage };
        },
    ],
    (clientStorage, { accountId, isMainPage }) => {
        if (isMainPage) {
            return clientStorage.generalAdvancedFilters || initialAdvancedFiltersEmpty;
        } else {
            return (
                clientStorage?.accountAdvancedFilters?.[accountId] || initialAdvancedFiltersEmpty
            );
        }
    },
);

export const selectSymbolsFilter = createDeepEqualSelector(
    [
        selectClientStorage,
        (state, { accountId, isMainPage = false }: { accountId: number; isMainPage?: boolean }) => {
            return { accountId, isMainPage };
        },
    ],
    (clientStorage, { accountId, isMainPage }) => {
        if (isMainPage) {
            return clientStorage.generalSymbolSelection || initialSymbolsEmpty;
        } else {
            return clientStorage?.accountSymbolSelection?.[accountId] || initialSymbolsEmpty;
        }
    },
);

export const selectDateFilter = createDeepEqualSelector(
    [
        selectClientStorage,
        (state, { accountId, isMainPage = false }: { accountId: number; isMainPage?: boolean }) => {
            return { accountId, isMainPage };
        },
    ],
    (clientStorage, { accountId, isMainPage }) => {
        if (isMainPage) {
            return clientStorage?.generalDateSelection || initialDate;
        } else {
            return clientStorage?.accountDateSelection?.[accountId] || initialDate;
        }
    },
);

export const selectAccountsFilter = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) => clientStorage.selectedAccounts || initialSymbolsEmpty,
);

export const selectWidgetsLayout = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) => clientStorage.widgetsLayout || [],
);

export const selectIsLiquidationSoundMuted = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) => clientStorage.isLiquidationSoundMuted ?? false,
);

export const selectWalletDisplayMode = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) => clientStorage.walletDisplayMode ?? WALLET_DISPLAY_MODE.NAMES,
);

// Address mode is a logged-in only preference; guests (incl. right after logout) always see names
export const selectIsAddressDisplayMode = createDeepEqualSelector(
    [selectWalletDisplayMode, selectMemberProfile],
    (walletDisplayMode, member) =>
        member.id !== null &&
        member.verified &&
        walletDisplayMode === WALLET_DISPLAY_MODE.ADDRESSES,
);

export const selectWalletCohortsByPerpEquityInnerSettings = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) =>
        clientStorage.widgetsLayout.find(
            (widget) => widget.id === WIDGETS.WALLET_COHORTS_BY_PERP_EQUITY,
        )?.innerSettings || [],
);

export const selectWalletCohortsByAllTimePnl = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) =>
        clientStorage.widgetsLayout.find(
            (widget) => widget.id === WIDGETS.WALLET_COHORTS_BY_ALL_TIME_PERP_PNL,
        )?.innerSettings || [],
);

export const selectStatsInnerSettings = createDeepEqualSelector(
    [selectClientStorage],
    (clientStorage) =>
        clientStorage.widgetsLayout.find((widget) => widget.id === WIDGETS.STATS)?.innerSettings ||
        [],
);

export const selectInnerSettingsChanged = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => clientStorage.innerSettingsChanged,
);

export const selectSelectedLeaderboardsTab = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => clientStorage.selectedLeaderboardsTab,
);

export const selectHeatmapDisabledColumns = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => clientStorage.heatmapDisabledColumns,
);

export const selectSelectedTrendingWalletsTab = createDeepEqualSelector(
    [clientStorageSelector],
    (clientStorage) => clientStorage.selectedTrendingWalletsTab,
);
